// src/components/ProfileView.tsx
import React from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { xpProgress as getXPProgress, getAnglerRank } from "../utils/xp";
import {
  getFeaturedBadges,
  getBadgeDisplay,
  buildReputationSummary,
  extractTrackValuesFromSummary,
  TRACK_LABELS,
  TIER_LABELS,
} from "../utils/reputation";

import type {
  UserProfile,
  LicenseInfo,
  ToastType,
  MarketplaceItem,
  CommunityPost,
  AppUser,
  ReputationSummary,
  ReputationTrackKey,
  ReputationTier,
  ReputationTrackState,
  CosmeticSlot,
} from "../types";

import { View } from "../types";

import {
  XIcon,
  ChevronDownIcon,
  RoddoFishIcon,
} from "./icons";

import { getRoddoUiTokens, resolveRoddoDarkMode } from "../constants/theme";
import AddLicenseModal from "./AddLicenseModal";
import RoddoPillTabs from "./RoddoPillTabs";
import {
  getEquippedCosmetics,
  getCosmeticSlotLabel,
  getPreviewShopCosmetics,
} from "../constants/cosmetics";

type NavState = Record<string, any>;

interface ProfileViewProps {
  userProfile: UserProfile;

  navigateTo: (view: View, state?: NavState) => void;

  users?: Record<string, AppUser>;
  posts?: CommunityPost[];

  onLicenseClick?: (license: any) => void;
  onAddLicense?: (() => void) | ((license: LicenseInfo) => void);
  onEditProfile?: () => void;
  onNotificationSettings?: () => void;
  onOpenSettings?: () => void;
  onManageGear?: () => void;

  onViewLicenseDetail?: (license: LicenseInfo) => void;

  userLicenses: LicenseInfo[];

  currency?: string;

  viewState?: {
    targetId?: string;
    openLicenseModalOnLoad?: boolean;
    profileUserId?: string;
  };

  addToast: (message: string, type?: ToastType) => void;

  marketplaceItems: MarketplaceItem[];
  onPurchaseItem: (item: MarketplaceItem) => void;

  gearItems?: any[];
  isDarkMode?: boolean;
}

const US_STATE_NAME_TO_CODE: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS",
  kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD", massachusetts: "MA",
  michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND",
  ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI",
  "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI",
  wyoming: "WY", "district of columbia": "DC", "washington, dc": "DC", "d.c.": "DC",
};

const OFFICIAL_FISHING_LICENSE_URLS: Record<string, string> = {
  CA: "https://wildlife.ca.gov/Licensing",
};

const US_STATE_CODES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM",
  "NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

function normalizeStateToCode(input?: string | null): string | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const up = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(up)) return up;
  return US_STATE_NAME_TO_CODE[raw.toLowerCase()] || null;
}

function buildOfficialLicenseUrl(stateCode: string): string {
  const code = String(stateCode || "").toUpperCase() || "CA";
  if (OFFICIAL_FISHING_LICENSE_URLS[code]) return OFFICIAL_FISHING_LICENSE_URLS[code];
  const q = encodeURIComponent(`${code} official fishing license buy renew`);
  return `https://www.google.com/search?q=${q}`;
}

/* ------------------------------------------------------------
 * Helpers (must be OUTSIDE components so they’re in scope)
 * ---------------------------------------------------------- */

const REPUTATION_TIER_LABELS: Record<ReputationTier, string> =
  TIER_LABELS as Record<ReputationTier, string>;

function isReputationTier(value: unknown): value is ReputationTier {
  return (
    value === "none" ||
    value === "bronze" ||
    value === "silver" ||
    value === "gold" ||
    value === "platinum" ||
    value === "mythic"
  );
}

function getTierLabel(tier: ReputationTier | null | undefined) {
  return tier && isReputationTier(tier)
    ? REPUTATION_TIER_LABELS[tier]
    : "Unranked";
}

function maskId(id: any) {
  const s = String(id ?? "").trim();
  if (!s) return "";
  if (s.length <= 6) return s;
  return `••••${s.slice(-4)}`;
}

function resolveProfileImageUrl(profile: any): string {
  return String(
    profile?.avatar ||
      profile?.avatarUrl ||
      profile?.avatarURL ||
      profile?.photoURL ||
      profile?.photoUrl ||
      profile?.profilePhotoUrl ||
      profile?.imageUrl ||
      "",
  ).trim();
}

function withAvatarVersion(url: string, version?: any): string {
  const clean = String(url || "").trim();
  if (!clean) return "";

  const v = Number(version || 0);
  if (!Number.isFinite(v) || v <= 0) return clean;

  if (clean.startsWith("data:") || clean.startsWith("blob:")) return clean;
  return `${clean}${clean.includes("?") ? "&" : "?"}v=${encodeURIComponent(String(v))}`;
}

function clampPct(n: number) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function normalizeAnalyticsText(value: any): string {
  return String(value ?? "").trim();
}

function titleCaseTiny(value: any): string {
  const raw = normalizeAnalyticsText(value);
  if (!raw) return "";
  return raw
    .split(/\s+/)
    .map((part) =>
      part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : part,
    )
    .join(" ");
}

function entryMillis(value: any): number {
  if (!value) return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value?.toMillis === "function") {
    try {
      const m = value.toMillis();
      return Number.isFinite(m) ? m : 0;
    } catch {}
  }
  if (typeof value?.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
    } catch {}
  }
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function inferTimeWindowFromEntry(entry: any): string {
  const explicit = normalizeAnalyticsText(
    entry?.timeBucket || entry?.window || entry?.fishIQTimeBucket,
  );
  if (explicit) return titleCaseTiny(explicit);

  const rawTime = normalizeAnalyticsText(entry?.time || entry?.catchTime);
  let hour: number | null = null;
  if (rawTime) {
    const h = Number(rawTime.split(":")[0]);
    if (Number.isFinite(h)) hour = h;
  }
  if (hour === null) {
    const ms = entryMillis(
      entry?.catchTimestamp ??
        entry?.timestamp ??
        entry?.date ??
        entry?.createdAt,
    );
    if (ms) hour = new Date(ms).getHours();
  }
  if (hour === null) return "";
  if (hour >= 4 && hour < 8) return "Dawn";
  if (hour >= 8 && hour < 12) return "Morning";
  if (hour >= 12 && hour < 17) return "Afternoon";
  if (hour >= 17 && hour < 21) return "Evening";
  return "Night";
}

function pushCount(map: Record<string, number>, value: any) {
  const clean = titleCaseTiny(value);
  if (!clean) return;
  map[clean] = (map[clean] || 0) + 1;
}

function topCounts(map: Record<string, number>, limit = 4) {
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));
}

function buildPersonalFishIQSummary(userProfile: any, posts?: any[]) {
  const logEntries = Array.isArray(userProfile?.log) ? userProfile.log : [];
  const diaryEntries = Array.isArray(userProfile?.catchDiary)
    ? userProfile.catchDiary
    : [];
  const catches = [...logEntries, ...diaryEntries].filter(Boolean);

  const bySpecies: Record<string, number> = {};
  const byWindow: Record<string, number> = {};
  const byCondition: Record<string, number> = {};
  const byWater: Record<string, number> = {};
  const byLure: Record<string, number> = {};

  let insightsAvailable = 0;
  let insightsSeen = 0;
  let insightsDismissed = 0;

  catches.forEach((entry: any) => {
    pushCount(bySpecies, entry?.species);
    pushCount(byWindow, inferTimeWindowFromEntry(entry));
    pushCount(
      byWater,
      entry?.waterName || entry?.location || entry?.water || entry?.spotName,
    );
    pushCount(
      byLure,
      entry?.lureOrBait || entry?.lure || entry?.bait || entry?.lureName,
    );

    const conditionRaw = [
      entry?.weather,
      entry?.waterCondition,
      entry?.conditions,
    ]
      .filter(Boolean)
      .join(" • ");
    normalizeAnalyticsText(conditionRaw)
      .split(/[•,|/]+/)
      .map((v) => v.trim())
      .filter(Boolean)
      .slice(0, 3)
      .forEach((v) => pushCount(byCondition, v));

    if (
      entry?.fishIQInsight ||
      entry?.insight ||
      entry?.insightSummary ||
      entry?.fishIQRead
    )
      insightsAvailable += 1;
    if (
      entry?.fishIQInsightSeen ||
      entry?.insightSeen ||
      entry?.hasOpenedInsight
    )
      insightsSeen += 1;
    if (
      entry?.fishIQInsightDismissed ||
      entry?.insightDismissed ||
      entry?.hideInsight
    )
      insightsDismissed += 1;
  });

  const recentInsightPosts = (posts || [])
    .filter(
      (p: any) =>
        p?.authorId === userProfile?.uid || p?.uid === userProfile?.uid,
    )
    .filter(
      (p: any) => p?.communitySignal || p?.patternSignal || p?.sourceCatchId,
    ).length;

  const sampleSize = catches.length;
  const signalLabel =
    sampleSize >= 12
      ? "Building pattern"
      : sampleSize >= 4
        ? "Early signal"
        : "Needs more catches";

  return {
    sampleSize,
    signalLabel,
    species: topCounts(bySpecies),
    windows: topCounts(byWindow),
    conditions: topCounts(byCondition),
    waters: topCounts(byWater),
    lures: topCounts(byLure),
    insightsAvailable,
    insightsSeen,
    insightsDismissed,
    recentInsightPosts,
  };
}

/* ------------------------------------------------------------
 * Tiny "..." menu (cute + consistent)
 * ---------------------------------------------------------- */
function KebabMenu({
  items,
  align = "right",
}: {
  items: Array<{ label: string; onClick: () => void; tone?: "danger" }>;
  align?: "left" | "right";
}) {
  const [open, setOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number } | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const positionMenu = React.useCallback(() => {
    const button = buttonRef.current;
    if (!button || typeof window === "undefined") return;

    const rect = button.getBoundingClientRect();
    const width = 188;
    const estimatedHeight = Math.max(52, items.length * 42 + 8);
    const gap = 8;
    const viewportPadding = 10;

    const preferredLeft =
      align === "right" ? rect.right - width : rect.left;
    const left = Math.min(
      window.innerWidth - width - viewportPadding,
      Math.max(viewportPadding, preferredLeft),
    );

    const roomBelow = window.innerHeight - rect.bottom - viewportPadding;
    const openAbove = roomBelow < estimatedHeight + gap && rect.top > estimatedHeight + gap;
    const top = openAbove
      ? Math.max(viewportPadding, rect.top - estimatedHeight - gap)
      : Math.min(
          window.innerHeight - estimatedHeight - viewportPadding,
          rect.bottom + gap,
        );

    setMenuPos({ top, left });
  }, [align, items.length]);

  React.useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }

    positionMenu();

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onScroll = () => setOpen(false);
    const onResize = () => setOpen(false);

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, positionMenu]);

  const menu =
    "fixed w-[188px] rounded-[12px] overflow-hidden " +
    "border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light-strong)] " +
    "shadow-[0_8px_24px_rgba(89,65,37,0.12)] p-1 z-[10020] " +
    "dark:border-[rgba(132,151,190,0.18)] dark:bg-[#0C1423] " +
    "dark:shadow-[0_10px_28px_rgba(0,0,0,0.30)]";

  const menuItem =
    "w-full text-left px-3 py-2.5 rounded-[9px] text-xs font-extrabold transition-colors " +
    "text-[var(--roddo-text-light)] hover:bg-black/[0.045] active:bg-black/[0.07] " +
    "dark:text-[var(--roddo-text-dark)] dark:hover:bg-[#101827] dark:active:bg-[#151F31]";

  const kebabBtn =
    "grid place-items-center w-9 h-9 rounded-[12px] " +
    "border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light)] " +
    "shadow-[0_3px_10px_rgba(89,65,37,0.06)] " +
    "dark:border-[rgba(132,151,190,0.16)] dark:bg-[#0C1423] " +
    "dark:shadow-[0_3px_10px_rgba(0,0,0,0.14)] " +
    "text-[var(--roddo-text-light)] hover:bg-black/[0.03] active:scale-[0.97] dark:text-[var(--roddo-text-dark)] dark:hover:bg-[#101827]";

  const menuNode =
    open && menuPos ? (
      <div
        ref={menuRef}
        className={menu}
        style={{ top: menuPos.top, left: menuPos.left }}
        role="menu"
        aria-label="Profile actions"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {items.map((it, idx) => (
          <button
            key={idx}
            type="button"
            role="menuitem"
            className={`${menuItem} ${
              it.tone === "danger"
                ? "text-red-600 hover:bg-red-50/70 dark:text-rose-300 dark:hover:bg-rose-500/15"
                : "text-[var(--profile-text-primary)]"
            }`}
            onClick={() => {
              setOpen(false);
              it.onClick();
            }}
          >
            {it.label}
          </button>
        ))}
      </div>
    ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Menu"
        aria-haspopup="menu"
        aria-expanded={open}
        className={kebabBtn}
      >
        ⋯
      </button>

      {typeof document !== "undefined" && menuNode
        ? ReactDOM.createPortal(menuNode, document.body)
        : menuNode}
    </>
  );
}

function ReputationRibbon({
  reputation,
}: {
  reputation?: ReputationSummary | null;
}) {
  const badges = getFeaturedBadges(reputation);
  if (!badges.length) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {badges.map((badge) => (
        <div
          key={badge.key}
          className="inline-flex items-center gap-2 rounded-full border border-[#4B2E86]/45 bg-[#3B2B6F] px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.10em] text-[#FFF8D7] shadow-[0_3px_10px_rgba(45,31,92,0.12)] dark:border-[#F2B84B]/35 dark:bg-[rgba(242,184,75,.14)] dark:text-[#F7D98E] dark:shadow-[0_3px_10px_rgba(0,0,0,0.14)]"
          title={badge.label}
        >
          <span className="text-sm leading-none drop-shadow">{badge.icon}</span>
          <span>{badge.label}</span>
        </div>
      ))}
    </div>
  );
}

function ReputationTrackCard({
  track,
}: {
  track: {
    key: ReputationTrackKey;
    value: number;
    tier: ReputationTier;
    nextTier: ReputationTier | null;
    nextThreshold: number | null;
    progressWithinTierPct: number;
  };
}) {
  const badge = getBadgeDisplay(track.key, track.tier);
  const progress = Math.max(0, Math.min(100, track.progressWithinTierPct || 0));
  const remainingLabel =
    track.nextTier && track.nextThreshold != null
      ? `${Math.max(0, track.nextThreshold - track.value)} to ${getTierLabel(track.nextTier)}`
      : "Max tier reached";

  return (
    <div
      className={
        "relative overflow-hidden rounded-[14px] border border-[var(--roddo-border-light)] " +
        "bg-[var(--roddo-panel-light)] px-3.5 py-3 shadow-[0_4px_12px_rgba(89,65,37,0.06),inset_0_1px_0_var(--roddo-ring-light)] " +
        "dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark)] " +
        "dark:shadow-[0_4px_12px_rgba(0,0,0,0.12),inset_0_1px_0_var(--roddo-ring-dark)]"
      }
    >
      

      <div className="relative flex items-center gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] border border-black/10 bg-[var(--profile-accent-soft)] text-lg shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:border-white/10 dark:from-[#FFF4C4]/90 dark:to-[#7CF0C4]/80">
          {badge?.icon ?? "✨"}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-black leading-tight text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                {TRACK_LABELS[track.key]}
              </div>
              <div className="mt-0.5 text-[11px] font-bold leading-snug text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                {track.value.toLocaleString()} total · {remainingLabel}
              </div>
            </div>

            <div className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#745B20]/45 bg-[#FFF2BE] px-2.5 py-1 text-[11px] font-black text-[#3A2F17] shadow-[0_2px_8px_rgba(58,47,23,0.10)] dark:border-[#FFF2BE]/55 dark:bg-[#FFF2BE] dark:text-[#3A2F17]">
              <span>{getTierLabel(track.tier)}</span>
            </div>
          </div>

          <div className="mt-2.5 relative h-2.5 overflow-hidden rounded-full border border-black/10 bg-[#E8DFD2] shadow-[inset_0_1px_0_rgba(255,255,255,0.55)] dark:border-white/10 dark:bg-white/10">
            
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-[var(--profile-accent)] transition-[width] duration-500"
              style={{ width: `${progress}%` }}
            >
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function safeProfileNumber(value: any, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

function getProfileCatchSpecies(entry: any): string {
  return String(
    entry?.species ||
      entry?.speciesName ||
      entry?.fishSpecies ||
      entry?.fish ||
      entry?.catchSpecies ||
      "",
  )
    .trim()
    .toLowerCase();
}

function getProfileCatchKey(entry: any, index: number): string {
  const species = getProfileCatchSpecies(entry);
  return String(
    entry?.id ||
      entry?.docId ||
      entry?.catchId ||
      entry?.logId ||
      entry?.createdAt ||
      entry?.timestamp ||
      `${species}-${entry?.date || entry?.waterName || entry?.location || index}`,
  );
}

function collectUniqueProfileCatches(profile: any): any[] {
  const buckets = [
    profile?.catchEntries,
    profile?.catchDiary,
    profile?.catchDiaryEntries,
    profile?.catchLog,
    profile?.log,
    profile?.catches,
  ];

  const seen = new Set<string>();
  const out: any[] = [];

  buckets.forEach((bucket) => {
    if (!Array.isArray(bucket)) return;

    bucket.forEach((entry: any, index: number) => {
      if (!entry || typeof entry !== "object") return;

      const key = getProfileCatchKey(entry, index);
      if (!key || seen.has(key)) return;

      seen.add(key);
      out.push(entry);
    });
  });

  return out;
}

type ProfilePrestigeTier =
  | "driftwood"
  | "harbor"
  | "silver"
  | "gold"
  | "mythic";

const PROFILE_TIER_ORDER: Record<ProfilePrestigeTier, number> = {
  driftwood: 0,
  harbor: 1,
  silver: 2,
  gold: 3,
  mythic: 4,
};

const REPUTATION_TIER_ORDER_LOCAL: Record<ReputationTier, number> = {
  none: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
  platinum: 4,
  mythic: 5,
};

function getHighestReputationTier(
  tracks: ReputationTrackState[],
): ReputationTier {
  return (tracks || []).reduce<ReputationTier>((best, track) => {
    const next = isReputationTier(track?.tier) ? track.tier : "none";
    return REPUTATION_TIER_ORDER_LOCAL[next] > REPUTATION_TIER_ORDER_LOCAL[best]
      ? next
      : best;
  }, "none");
}

function getProfilePrestigeTier(args: {
  level: number;
  totalCatches: number;
  uniqueSpecies: number;
  completedChallenges: number;
  highestReputationTier: ReputationTier;
  isFounding5k: boolean;
}): ProfilePrestigeTier {
  const reputationOrder =
    REPUTATION_TIER_ORDER_LOCAL[args.highestReputationTier] || 0;
  if (
    args.level >= 30 ||
    reputationOrder >= 5 ||
    args.completedChallenges >= 75
  )
    return "mythic";
  if (
    args.level >= 20 ||
    reputationOrder >= 3 ||
    args.uniqueSpecies >= 75 ||
    args.completedChallenges >= 30
  )
    return "gold";
  if (
    args.level >= 10 ||
    reputationOrder >= 2 ||
    args.uniqueSpecies >= 25 ||
    args.totalCatches >= 75
  )
    return "silver";
  if (
    args.level >= 4 ||
    reputationOrder >= 1 ||
    args.isFounding5k ||
    args.totalCatches >= 10
  )
    return "harbor";
  return "driftwood";
}

function getProfilePrestigeCopy(tier: ProfilePrestigeTier) {
  if (tier === "mythic") {
    return {
      label: "Mythic Tide Frame",
      title: "Big Blue Vanguard",
      icon: "🌌",
      subtitle: "A prestige profile forged through deep water time.",
    };
  }
  if (tier === "gold") {
    return {
      label: "Golden Tide Frame",
      title: "Tide Proven Angler",
      icon: "🌞",
      subtitle: "A high-trust angler card with earned field marks.",
    };
  }
  if (tier === "silver") {
    return {
      label: "Silver Wake Frame",
      title: "Pattern Builder",
      icon: "🌊",
      subtitle: "Signals are forming across catches, waters, and species.",
    };
  }
  if (tier === "harbor") {
    return {
      label: "Harbor Bronze Frame",
      title: "New Water Scout",
      icon: "⚓",
      subtitle: "Early momentum with real catches and exploration.",
    };
  }
  return {
    label: "Driftwood Starter Frame",
    title: "New Angler",
    icon: "🪵",
    subtitle: "Start logging catches to evolve this profile frame.",
  };
}

function getProfilePrestigePalette(tier: ProfilePrestigeTier, isDark: boolean) {
  if (tier === "mythic") {
    return {
      glow: isDark ? "rgba(196,181,253,0.32)" : "rgba(139,123,255,0.22)",
      border: isDark ? "rgba(216,180,254,0.46)" : "rgba(139,123,255,0.36)",
      banner: isDark
        ? "linear-gradient(135deg, #101827, rgba(103,216,232,0.08))"
        : "linear-gradient(135deg, #F1E8FF, #DFF7FF, #FFE9F7)",
      frame: isDark
        ? "linear-gradient(135deg, #D8B4FE, #7DD3FC, #FDE68A)"
        : "linear-gradient(135deg, #8B7BFF, #67D8FF, #FFD76A)",
      text: isDark ? "#F8FAFF" : "#33245F",
    };
  }
  if (tier === "gold") {
    return {
      glow: isDark ? "rgba(255,215,106,0.34)" : "rgba(246,196,83,0.28)",
      border: isDark ? "rgba(255,215,106,0.42)" : "rgba(212,155,34,0.34)",
      banner: isDark
        ? "linear-gradient(135deg, #101827, rgba(242,184,75,0.09))"
        : "linear-gradient(135deg, #FFF4C7, #FFE2A3, #EAF6FF)",
      frame: "linear-gradient(135deg, #FFF0B8, #FFD76A, #F6C453)",
      text: isDark ? "#FFF1B6" : "#5F4300",
    };
  }
  if (tier === "silver") {
    return {
      glow: isDark ? "rgba(124,199,255,0.28)" : "rgba(124,199,255,0.22)",
      border: isDark ? "rgba(191,219,254,0.36)" : "rgba(124,199,255,0.34)",
      banner: isDark
        ? "linear-gradient(135deg, #101827, rgba(103,216,232,0.08))"
        : "linear-gradient(135deg, #F3F7FF, #DFF1FF, #F8F5FF)",
      frame: "linear-gradient(135deg, #F8FAFC, #BFD7FF, #7CC7FF)",
      text: isDark ? "#EAF6FF" : "#17405F",
    };
  }
  if (tier === "harbor") {
    return {
      glow: isDark ? "rgba(255,215,106,0.22)" : "rgba(255,215,106,0.20)",
      border: isDark ? "rgba(255,215,106,0.30)" : "rgba(185,142,62,0.30)",
      banner: isDark
        ? "linear-gradient(135deg, #101827, rgba(242,184,75,0.07))"
        : "linear-gradient(135deg, #FFF0B8, #EAF6FF, #F9F2E9)",
      frame: "linear-gradient(135deg, #FFD76A, #E7B85C, #A97435)",
      text: isDark ? "#FFE8A3" : "#694B09",
    };
  }
  return {
    glow: isDark ? "rgba(255,255,255,0.12)" : "rgba(164,112,53,0.16)",
    border: isDark ? "rgba(255,255,255,0.18)" : "rgba(116,86,54,0.22)",
    banner: isDark
      ? "#101827"
      : "linear-gradient(135deg, #FFF9F1, #F3E6D2)",
    frame: "linear-gradient(135deg, #D4A76A, #B88445, #7B5630)",
    text: isDark ? "#F8FAFF" : "#3C3024",
  };
}

function getTrackIconFromKey(key: ReputationTrackKey) {
  if (key === "species_mastery") return "🐟";
  if (key === "explorer") return "🧭";
  if (key === "community_contributor") return "💬";
  if (key === "bazaar_trader") return "🛒";
  if (key === "tournament_competitor") return "🏆";
  return "✨";
}

const ProfileView: React.FC<ProfileViewProps> = (props) => {
  const {
    userProfile,
    navigateTo,
    users,
    posts,
    userLicenses,
    onLicenseClick,
    onEditProfile,
    onNotificationSettings,
    onOpenSettings,
    onManageGear,
    onViewLicenseDetail,
    viewState,
    addToast,
    gearItems,
    isDarkMode,
  } = props;

  const { t, i18n } = useTranslation();
  const isDark = resolveRoddoDarkMode(isDarkMode);
  const ui = getRoddoUiTokens(isDark) as any;
  const profileVars = {
    "--profile-page-bg": ui.pageBg ?? "transparent",
    "--profile-card-bg": ui.cardBgStrong ?? ui.cardBg,
    "--profile-surface-bg": ui.cardBgMuted ?? ui.surfaceBgSoft ?? ui.cardBg,
    "--profile-text-primary": ui.textPrimary,
    "--profile-text-secondary": ui.textSecondary,
    "--profile-border": ui.border,
    "--profile-accent": isDark ? "#8A70F2" : "#2F80ED",
    "--profile-accent-soft": isDark ? "rgba(138,112,242,0.11)" : "rgba(47,128,237,0.09)",
    "--profile-accent-border": isDark ? "rgba(138,112,242,0.28)" : "rgba(47,128,237,0.28)",
    "--profile-sand": isDark ? "#0F1828" : "#F3E8D8",
    "--profile-sand-strong": isDark ? "#151F31" : "#EADBC6",
    "--roddo-panel-light":
      ui.cardBg ??
      "#FFFCF7",
    "--roddo-panel-light-strong":
      ui.cardBgStrong ??
      ui.cardBg ??
      "#FFFCF7",
    "--roddo-panel-dark":
      ui.cardBgMuted ??
      ui.cardBg ??
      "#101827",
    "--roddo-panel-dark-strong":
      ui.cardBgStrong ??
      ui.cardBg ??
      "#151F31",
    "--roddo-border-light": isDark
      ? "rgba(255,255,255,0.16)"
      : "rgba(116,86,54,0.22)",
    "--roddo-border-dark": "rgba(132,151,190,0.17)",
    "--roddo-text-light": ui.textPrimary ?? "#2b2b2b",
    "--roddo-text-light-soft": ui.textSecondary ?? "rgba(43,43,43,0.74)",
    "--roddo-text-dark": ui.textPrimary ?? "#F8FAFF",
    "--roddo-text-dark-soft": ui.textSecondary ?? "rgba(232,236,255,0.88)",
    "--roddo-shadow-light":
      "0 14px 0 rgba(89,65,37,0.10), 0 22px 42px rgba(89,65,37,0.10)",
    "--roddo-shadow-strong-light":
      "0 16px 0 rgba(89,65,37,0.10), 0 28px 58px rgba(89,65,37,0.14)",
    "--roddo-shadow-dark":
      "0 5px 14px rgba(0,0,0,0.14)",
    "--roddo-shadow-strong-dark":
      "0 8px 22px rgba(0,0,0,0.22)",
    "--roddo-ring-light": "rgba(255,255,255,0.58)",
    "--roddo-ring-dark": "rgba(255,255,255,0.035)",
  } as React.CSSProperties;

  const SHOW_MARKETPLACE = false; // gated

  const [isLicenseModalOpen, setIsLicenseModalOpen] = React.useState(false);
  const [isAddLicenseModalOpen, setIsAddLicenseModalOpen] = React.useState(false);
  const [gearExpanded, setGearExpanded] = React.useState<
    null | "rod" | "reel" | "lure"
  >(null);
  const [showReputationDetails, setShowReputationDetails] =
    React.useState(false);
  const [profilePage, setProfilePage] = React.useState<"overview" | "avatar">("overview");

  // ────────────────────────────────────────────────────────────────────────────
  // SURFACE TOKENS (match TackleBox: creamy light, indigo glass dark)
  // ────────────────────────────────────────────────────────────────────────────
  const creamCard =
    "relative isolate overflow-visible rounded-[16px] border border-[var(--roddo-border-light)] transition-[background,border-color,box-shadow,color,opacity,transform] duration-200 ease-out " +
    "bg-[var(--roddo-panel-light-strong)] text-[var(--roddo-text-light)] shadow-[0_7px_18px_rgba(89,65,37,0.07)] " +
    "dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark-strong)] dark:text-[var(--roddo-text-dark)] dark:shadow-[0_5px_14px_rgba(0,0,0,0.14)] " +
    "p-4 sm:p-5";

  const innerCard =
    "relative overflow-visible rounded-[14px] border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light)] transition-[background,border-color,box-shadow,color,opacity,transform] duration-200 ease-out " +
    "shadow-[0_4px_12px_rgba(89,65,37,0.06)] dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.12)]";

  const rowButton =
    "w-full text-left rounded-[14px] px-4 py-3 transition-[background,border-color,box-shadow,color,opacity,transform] duration-180 ease-out " +
    "border border-[var(--roddo-border-light)] bg-[var(--roddo-panel-light)] " +
    "shadow-[0_4px_14px_rgba(89,65,37,0.07)] hover:-translate-y-px active:translate-y-0 " +
    "dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.12)]";

  const chip =
    "inline-flex items-center gap-2 rounded-full px-3 py-1 transition-[background,border-color,box-shadow,color] duration-180 ease-out " +
    "bg-[var(--roddo-panel-light)] border border-[var(--roddo-border-light)] " +
    "shadow-[0_3px_10px_rgba(89,65,37,0.06),inset_0_1px_0_rgba(255,255,255,0.52)] " +
    "dark:bg-[var(--roddo-panel-dark)] dark:border-[var(--roddo-border-dark)] dark:shadow-none";

  const getLicensePhoto = (l: any): string => {
    return (
      l?.photoUrl ||
      l?.imageUrl ||
      l?.photo ||
      l?.licensePhotoUrl ||
      l?.licenseImageUrl ||
      l?.image ||
      ""
    );
  };

  // ------------------------------------------------------------
  // VIEW MODE: My Profile vs User Feed
  // ------------------------------------------------------------
  const profileUserId = viewState?.profileUserId;
  const isViewingOtherUser =
    !!profileUserId && profileUserId !== userProfile.uid;
  const viewedUserId = isViewingOtherUser ? profileUserId! : userProfile.uid;

  const viewedUserFromUsers = users?.[viewedUserId];
  const viewedUserName =
    viewedUserFromUsers?.name ||
    (isViewingOtherUser ? "Angler" : userProfile.name || "Angler");

  const viewedUserHandle = String(
    (viewedUserFromUsers as any)?.username ||
      (viewedUserFromUsers as any)?.handle ||
      (!isViewingOtherUser
        ? (userProfile as any)?.username || (userProfile as any)?.handle
        : "") ||
      "",
  ).trim();

  // When viewing yourself, trust the live userProfile first. The broad users map can lag
  // behind Firestore/cache by one tick after avatar upload and would otherwise mask the
  // optimistic update until a full app refresh.
  const viewedUserAvatar = isViewingOtherUser
    ? withAvatarVersion(
        resolveProfileImageUrl(viewedUserFromUsers),
        (viewedUserFromUsers as any)?.avatarUpdatedAt,
      )
    : withAvatarVersion(
        resolveProfileImageUrl(userProfile),
        (userProfile as any)?.avatarUpdatedAt,
      );

  const viewedUserPosts = React.useMemo(() => {
    const arr = (posts || []).filter((p: any) => p.authorId === viewedUserId);
    return arr.sort(
      (a: any, b: any) => (b.timestamp || 0) - (a.timestamp || 0),
    );
  }, [posts, viewedUserId]);

  // ------------------------------------------------------------
  // XP / LEVEL (canonical)
  // ------------------------------------------------------------
  const canonicalXP = Number(
    (userProfile as any)?.xp ?? (userProfile as any)?.points ?? 0,
  );
  const shellBalance = Math.max(0, Number((userProfile as any)?.shells ?? 0));
  const lifetimeShells = Math.max(
    shellBalance,
    Number((userProfile as any)?.lifetimeShells ?? shellBalance),
  );
  const equippedCosmetics = React.useMemo(
    () => getEquippedCosmetics((userProfile as any)?.cosmeticLoadout),
    [userProfile],
  );
  const featuredAvatarGear = equippedCosmetics;
  const equippedTitleCosmetic = equippedCosmetics.find(
    (item) => item.slot === "title",
  );
  const equippedFrameCosmetic = equippedCosmetics.find(
    (item) => item.slot === "profileFrame",
  );
  const xpInfo = getXPProgress(canonicalXP);

  const safeLevel = xpInfo.level;
  const progressPercentage = xpInfo.percent;
  const xpToNext = xpInfo.remaining;
  const anglerRank = xpInfo.rank || getAnglerRank(safeLevel);
  const previewAvatarShop = React.useMemo(
    () => getPreviewShopCosmetics(safeLevel, shellBalance),
    [safeLevel, shellBalance],
  );
  const currentLevelFloor = xpInfo.currentLevelXP;
  const nextLevelFloor = xpInfo.nextLevelXP;
  const levelSpan = Math.max(1, nextLevelFloor - currentLevelFloor);

  const isFounding5k =
    typeof (userProfile as any).memberNumber === "number" &&
    (userProfile as any).memberNumber <= 5000;

  // Active license (used in Licenses card)
  const activeLicense = (userLicenses || []).find((l: any) => !!l.active);

  const profileLog = Array.isArray((userProfile as any)?.log)
    ? (userProfile as any).log
    : [];
  const totalCatches =
    Number((userProfile as any)?.totalCatches) ||
    Number((userProfile as any)?.stats?.catches) ||
    Number((userProfile as any)?.stats?.totalCatches) ||
    Number((userProfile as any)?.catchCount) ||
    profileLog.length;

  const uniqueSpeciesLogged =
    Number((userProfile as any)?.stats?.speciesMasteryCount) ||
    Number((userProfile as any)?.stats?.uniqueSpeciesCount) ||
    Number((userProfile as any)?.stats?.uniqueSpecies) ||
    Number((userProfile as any)?.stats?.species) ||
    Number((userProfile as any)?.uniqueSpeciesCount) ||
    Number((userProfile as any)?.speciesCount) ||
    new Set(
      profileLog
        .map((entry: any) =>
          String(entry?.species || "")
            .trim()
            .toLowerCase(),
        )
        .filter(Boolean),
    ).size;

  const completedChallengesRaw =
    (userProfile as any)?.completedChallengesCount ??
    (userProfile as any)?.completedChallengesMap ??
    (userProfile as any)?.challengeCompletionMap ??
    (userProfile as any)?.completedChallenges;
  const completedChallengesCount =
    typeof completedChallengesRaw === "number"
      ? completedChallengesRaw
      : Array.isArray(completedChallengesRaw)
        ? completedChallengesRaw.length
        : completedChallengesRaw && typeof completedChallengesRaw === "object"
          ? Object.keys(completedChallengesRaw).length
          : Number((userProfile as any)?.stats?.completedChallenges ?? 0);

  const fallbackReputationValues = React.useMemo(
    () => ({
      species_mastery: Math.max(
        0,
        Number((userProfile as any)?.stats?.speciesMasteryCount ?? 0),
        Number((userProfile as any)?.stats?.uniqueSpeciesCount ?? 0),
        Number((userProfile as any)?.stats?.uniqueSpecies ?? 0),
        Number((userProfile as any)?.uniqueSpeciesCount ?? 0),
        uniqueSpeciesLogged,
      ),
      explorer: Math.max(
        0,
        Number((userProfile as any)?.stats?.uniqueWatersCount ?? 0),
        Number((userProfile as any)?.stats?.waters ?? 0),
        Number((userProfile as any)?.stats?.spots ?? 0),
      ),
      community_contributor: Math.max(
        0,
        Number((userProfile as any)?.stats?.sharedContributions ?? 0),
        Number((userProfile as any)?.stats?.communityPosts ?? 0),
        Number((userProfile as any)?.stats?.posts ?? 0),
      ),
      bazaar_trader: Math.max(
        0,
        Number((userProfile as any)?.stats?.bazaarSales ?? 0),
        Number((userProfile as any)?.stats?.bazaarTrades ?? 0),
        Number((userProfile as any)?.stats?.trades ?? 0),
      ),
      tournament_competitor: Math.max(
        0,
        Number((userProfile as any)?.stats?.tournamentsEntered ?? 0),
        Number((userProfile as any)?.stats?.tournaments ?? 0),
      ),
    }),
    [userProfile, uniqueSpeciesLogged],
  );

  const reputation: ReputationSummary = React.useMemo(() => {
    const existing = (userProfile as any)?.reputation;
    const existingValues = existing?.tracks
      ? extractTrackValuesFromSummary(existing)
      : null;

    const mergedValues = {
      species_mastery: Math.max(
        Number(existingValues?.species_mastery ?? 0),
        Number(fallbackReputationValues.species_mastery ?? 0),
      ),
      explorer: Math.max(
        Number(existingValues?.explorer ?? 0),
        Number(fallbackReputationValues.explorer ?? 0),
      ),
      community_contributor: Math.max(
        Number(existingValues?.community_contributor ?? 0),
        Number(fallbackReputationValues.community_contributor ?? 0),
      ),
      bazaar_trader: Math.max(
        Number(existingValues?.bazaar_trader ?? 0),
        Number(fallbackReputationValues.bazaar_trader ?? 0),
      ),
      tournament_competitor: Math.max(
        Number(existingValues?.tournament_competitor ?? 0),
        Number(fallbackReputationValues.tournament_competitor ?? 0),
      ),
    };

    return buildReputationSummary(mergedValues);
  }, [userProfile, fallbackReputationValues]);

  const featuredBadges = getFeaturedBadges(reputation);
  const reputationTracks = Object.values(
    reputation?.tracks ?? {},
  ) as ReputationTrackState[];
  const speciesMasteryValue =
    Number((reputation as any)?.tracks?.species_mastery?.value) ||
    Number((userProfile as any)?.stats?.speciesMasteryCount) ||
    uniqueSpeciesLogged;
  const explorerValue =
    Number((reputation as any)?.tracks?.explorer?.value) ||
    Number((userProfile as any)?.stats?.uniqueWatersCount) ||
    Number((userProfile as any)?.stats?.waters) ||
    Number((userProfile as any)?.stats?.spots) ||
    0;

  const fishIQSummary = React.useMemo(
    () =>
      buildPersonalFishIQSummary(
        userProfile as any,
        posts as any[] | undefined,
      ),
    [userProfile, posts],
  );

  const analyticsPillStyle = (
    accent: string,
    text: string,
  ): React.CSSProperties => ({
    background: accent,
    color: text,
    borderColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.08)",
  });

  React.useEffect(() => {
    if (viewState?.openLicenseModalOnLoad) {
      setIsLicenseModalOpen(true);
    }
    if (viewState?.targetId) {
      const license = (userLicenses || []).find(
        (l: any) => l.id === viewState.targetId,
      );
      if (license) {
        if (onViewLicenseDetail) onViewLicenseDetail(license);
        else onLicenseClick?.(license);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState?.openLicenseModalOnLoad, viewState?.targetId]);

  // ------------------------------------------------------------
  // OTHER USER FEED
  // ------------------------------------------------------------
  if (isViewingOtherUser) {
    return (
      <div
        className="p-4 pb-6"
        style={{ ...profileVars, background: ui.pageBg ?? "transparent" }}
      >
        <div className="max-w-3xl mx-auto space-y-3.5">
          <div className={creamCard}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-[#ffcd64] shadow-[0_4px_0_0_rgba(161,121,24,0.85)] bg-[var(--profile-surface-bg)]">
                {viewedUserAvatar ? (
                  <img
                    key={viewedUserAvatar}
                    src={viewedUserAvatar}
                    alt={viewedUserName}
                    className="w-full h-full object-cover"
                  />
                ) : null}
              </div>

              <div className="min-w-0">
                <div className="text-lg font-extrabold text-[var(--profile-text-primary)] truncate">
                  {viewedUserName}
                </div>
                <div className="text-[11px] text-[var(--profile-text-secondary)]">
                  Angler feed
                </div>
              </div>

              <div className="ml-auto">
                <button
                  type="button"
                  onClick={() => navigateTo(View.Community)}
                  className={`${chip} rounded-2xl px-4 py-2 text-sm font-extrabold transition hover:-translate-y-0.5 active:translate-y-[1px]`}
                >
                  Back
                </button>
              </div>
            </div>
          </div>

          <div className={creamCard}>
            <div className="text-sm font-extrabold text-[var(--profile-text-primary)]">
              Recent water moments
            </div>

            {viewedUserPosts.length === 0 ? (
              <div className="mt-2 text-sm text-[var(--profile-text-secondary)]">
                No water moments yet.
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {viewedUserPosts.map((post: any) => {
                  const bodyText = post.text ?? post.caption ?? "";
                  const imageUrl = post.imageUrl ?? post.image ?? undefined;

                  return (
                    <div key={post.id} className={`${innerCard} p-4`}>
                      <div className="text-[11px] text-[var(--profile-text-secondary)]">
                        {post.timestamp
                          ? new Date(post.timestamp).toLocaleString()
                          : ""}
                      </div>

                      {bodyText ? (
                        <div className="mt-1 text-sm text-[var(--profile-text-primary)] whitespace-pre-line">
                          {bodyText}
                        </div>
                      ) : null}

                      {imageUrl ? (
                        <div className="mt-3 overflow-hidden rounded-3xl border border-black/10 dark:border-white/10 bg-[var(--profile-surface-bg)] dark:bg-white/5">
                          <img
                            src={imageUrl}
                            alt="Post"
                            className="w-full max-h-[420px] object-cover"
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------
  // Official license finder — matches The Hold/Tackle Box behavior.
  // Add License remains handled by AddLicenseModal.
  // ------------------------------------------------------------
  const OfficialLicenseModal: React.FC = () => {
    const detectedStateCode =
      normalizeStateToCode((activeLicense as any)?.state) || "CA";
    const [licenseLinkState, setLicenseLinkState] = React.useState(detectedStateCode);

    const openOfficialLicenseSite = () => {
      try {
        const url = buildOfficialLicenseUrl(licenseLinkState || detectedStateCode || "CA");
        const opened = window.open(url, "_blank", "noopener,noreferrer");
        if (!opened) addToast("Popup blocked — allow popups to open the license site.", "info");
      } catch (err) {
        console.error("[ProfileView] official license link failed", err);
        addToast("Could not open license site.", "error");
      }
    };

    const modal = (
      <div
        className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/65 p-4"
        onClick={() => setIsLicenseModalOpen(false)}
        role="dialog"
        aria-modal="true"
        aria-label="Find official fishing license site"
      >
        <div
          className="w-full max-w-sm rounded-[16px] border p-4 shadow-[0_12px_32px_rgba(0,0,0,0.32)]"
          style={{
            background: ui.cardBgStrong ?? ui.cardBg,
            color: ui.textPrimary,
            borderColor: ui.borderStrong ?? ui.border,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.20em]" style={{ color: ui.textSecondary }}>
                Fishing license
              </div>
              <h2 className="mt-1 text-lg font-black">Find official license</h2>
              <p className="mt-1 text-[12px] font-semibold leading-5" style={{ color: ui.textSecondary }}>
                Choose your state, then open the official purchase or renewal route.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsLicenseModalOpen(false)}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border"
              style={{ background: ui.surfaceBgSoft ?? ui.cardBg, borderColor: ui.border }}
              aria-label="Close"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: ui.textSecondary }}>
              State
            </span>
            <select
              value={licenseLinkState}
              onChange={(event) => setLicenseLinkState(String(event.target.value || "CA"))}
              className="relative z-[1] w-full rounded-[12px] px-3 py-3 text-sm font-extrabold outline-none"
              style={{ background: ui.inputBg ?? ui.surfaceBgSoft ?? ui.cardBg, color: ui.textPrimary, border: `1px solid ${ui.borderStrong ?? ui.border}` }}
              aria-label="Select license state"
            >
              {US_STATE_CODES.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={openOfficialLicenseSite}
            className="mt-3 w-full rounded-[12px] px-4 py-3 text-sm font-black text-white transition active:scale-[0.985]"
            style={{ background: isDark ? "#8A70F2" : "#2F80ED", boxShadow: isDark ? "0 5px 14px rgba(65,52,145,0.20)" : "0 5px 14px rgba(47,128,237,0.16)" }}
          >
            Buy / renew official
          </button>

          <button
            type="button"
            onClick={() => {
              setIsLicenseModalOpen(false);
              setIsAddLicenseModalOpen(true);
            }}
            className="mt-2 w-full rounded-[12px] border px-4 py-3 text-sm font-black"
            style={{ background: ui.surfaceBgSoft ?? ui.cardBg, color: ui.textPrimary, borderColor: ui.border }}
          >
            I already have a license
          </button>

          <p className="mt-3 text-[10px] font-semibold leading-4" style={{ color: ui.textSecondary }}>
            Roddo opens the known official state page when available. Other states use the same official-site search fallback as The Hold.
          </p>
        </div>
      </div>
    );

    return typeof document !== "undefined" ? ReactDOM.createPortal(modal, document.body) : modal;
  };

  // ------------------------------------------------------------
  // MAIN PROFILE UI
  // ------------------------------------------------------------
  const lang = i18n?.language || "en";

  const heroActionButton =
    "inline-flex min-h-10 items-center justify-center rounded-[16px] border border-black/10 bg-[var(--profile-surface-bg)] px-4 py-2 text-sm font-bold text-[var(--profile-text-primary)] shadow-[0_5px_14px_rgba(89,65,37,0.08)] transition-[transform,filter,box-shadow] hover:-translate-y-px hover:brightness-[1.02] active:translate-y-0 active:scale-[0.98] dark:border-white/10 dark:shadow-[0_7px_18px_rgba(3,0,16,0.22)]";

  // ✅ Restore original gear derivation (this is what you “lost”)
  const allGearItems = (gearItems ||
    (userProfile as any)?.tackleBox ||
    (userProfile as any)?.tacklebox ||
    []) as any[];

  const gearCount = Array.isArray(allGearItems)
    ? allGearItems.length
    : Number((userProfile as any)?.gearCount || 0);

  const gearById = React.useMemo(() => {
    const map = new Map<string, any>();
    (allGearItems || []).forEach((g: any) => {
      const id = g?.id || g?.gearId || g?.docId;
      if (id) map.set(String(id), g);
    });
    return map;
  }, [allGearItems]);

  const gearLabel = (g: any): string | null => {
    if (!g) return null;
    const name =
      g?.name ||
      g?.title ||
      g?.model ||
      g?.label ||
      g?.brand ||
      g?.displayName ||
      null;

    const s = typeof name === "string" ? name.trim() : "";
    if (s) return s;

    const type = (g?.type || g?.category || "").toString().trim();
    const brand = (g?.brand || "").toString().trim();
    const fallback = [brand, type].filter(Boolean).join(" ");
    return fallback || null;
  };

  const equippedInfo = React.useMemo(() => {
    const eq =
      (userProfile as any)?.equippedGear ||
      (userProfile as any)?.equipped ||
      (userProfile as any)?.loadout ||
      null;

    const pickNameFromId = (id: any) => {
      const s = (id ?? "").toString().trim();
      if (!s) return null;
      const g = gearById.get(s);
      return gearLabel(g) || null;
    };

    const rodId =
      eq && typeof eq === "object"
        ? (eq as any).rodId || (eq as any).rod
        : null;
    const reelId =
      eq && typeof eq === "object"
        ? (eq as any).reelId || (eq as any).reel
        : null;
    const lureId =
      eq && typeof eq === "object"
        ? (eq as any).lureOrBaitId || (eq as any).lureId || (eq as any).lure
        : null;

    const rodName = rodId ? pickNameFromId(rodId) || "Equipped" : null;
    const reelName = reelId ? pickNameFromId(reelId) || "Equipped" : null;
    const lureName = lureId ? pickNameFromId(lureId) || "Equipped" : null;

    return {
      rod: { id: rodId ? String(rodId) : null, name: rodName },
      reel: { id: reelId ? String(reelId) : null, name: reelName },
      lure: { id: lureId ? String(lureId) : null, name: lureName },
    };
  }, [userProfile, gearById]);

  const equippedSlotCount = [
    equippedInfo.rod.name,
    equippedInfo.reel.name,
    equippedInfo.lure.name,
  ].filter(Boolean).length;

  const handleManageGear = React.useCallback(() => {
    if (onManageGear) {
      onManageGear();
      return;
    }

    // Safety fallback: older App.tsx builds sometimes rely on navigation instead of onManageGear.
    navigateTo(View.TackleBox);
  }, [navigateTo, onManageGear]);

  const handleManageAvatarGear = React.useCallback(() => {
    // Avatar gear should land directly on The Hold → Avatar, not the default Loadout tab.
    // Keep multiple state keys for compatibility with older/newer TackleBox tab readers.
    navigateTo(View.TackleBox, {
      tab: "avatar",
      activeTab: "avatar",
      initialTab: "avatar",
      tackleBoxTab: "avatar",
    });
  }, [navigateTo]);

  const handleOpenSettings = React.useCallback(() => {
    if (onOpenSettings) {
      onOpenSettings();
      return;
    }

    if (onNotificationSettings) {
      onNotificationSettings();
      return;
    }

    const settingsView =
      (View as any)?.NotificationSettings || (View as any)?.Settings;
    if (settingsView) {
      navigateTo(settingsView);
      return;
    }

    addToast?.("Settings are not wired in this build yet.", "error");
  }, [addToast, navigateTo, onNotificationSettings, onOpenSettings]);

  const handleOpenCrew = React.useCallback(() => {
    const crewView = (View as any)?.Crew;
    if (crewView) {
      navigateTo(crewView);
      return;
    }

    addToast?.("Crew deck is not wired in this build yet.", "error");
  }, [addToast, navigateTo]);

  const handleOpenRoddoRadio = React.useCallback(() => {
    const radioView = (View as any)?.RoddoRadio;
    if (radioView) {
      navigateTo(radioView);
      return;
    }

    addToast?.("Roddo Radio is not wired in this build yet.", "error");
  }, [addToast, navigateTo]);

  const sectionEyebrow =
    "text-[10px] font-black uppercase tracking-[0.22em] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]";
  const sectionIconChip =
    "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] transition-[background,border-color,box-shadow,color] duration-300 ease-out";

  const statusBadge = (active: boolean) =>
    active
      ? "border-[#9AD8B6] bg-[#EAF8F0] text-[#136B4A] dark:border-[#A7F3D0]/25 dark:bg-[#A7F3D0]/10 dark:text-[#A7F3D0]"
      : "border-[#F3C0C0] bg-[#FFF0F0] text-[#9A3D3D] dark:border-rose-300/20 dark:bg-rose-300/10 dark:text-rose-200";

  const sectionHeroChip = (
    background: string,
    color: string,
  ): React.CSSProperties => ({
    transition:
      "background 280ms ease, border-color 280ms ease, box-shadow 280ms ease, color 280ms ease, opacity 280ms ease",
    background,
    color,
    border: `1.5px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"}`,
    boxShadow: isDark
      ? "0 12px 24px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.08)"
      : "0 10px 22px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.58)",
  });

  const licenseCardShellStyle: React.CSSProperties = {
    background: ui.cardBgStrong ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    boxShadow: isDark ? "0 5px 14px rgba(0,0,0,0.13)" : "0 7px 18px rgba(89,65,37,0.07)",
  };

  const licenseRowStyle: React.CSSProperties = {
    background: ui.surfaceBgSoft ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
    boxShadow: "none",
  };

  const gearCardShellStyle: React.CSSProperties = {
    background: ui.cardBgStrong ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    boxShadow: isDark ? "0 5px 14px rgba(0,0,0,0.13)" : "0 7px 18px rgba(89,65,37,0.07)",
  };

  const publicBio = String((userProfile as any)?.bio || "").trim();

  const highestReputationTier = getHighestReputationTier(reputationTracks);
  const profilePrestigeTier = getProfilePrestigeTier({
    level: safeLevel,
    totalCatches,
    uniqueSpecies: speciesMasteryValue,
    completedChallenges: completedChallengesCount,
    highestReputationTier,
    isFounding5k,
  });
  const prestigeCopy = getProfilePrestigeCopy(profilePrestigeTier);
  const prestigePalette = getProfilePrestigePalette(
    profilePrestigeTier,
    isDark,
  );
  const hasEquippedProfileFrame = Boolean(equippedFrameCosmetic);
  const profileFramePillLabel = equippedFrameCosmetic
    ? `${equippedFrameCosmetic.emoji} ${equippedFrameCosmetic.name}`
    : "No profile frame";
  const neutralAvatarFrameStyle: React.CSSProperties = {
    background: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.72)",
    boxShadow: isDark
      ? "0 4px 12px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.04)"
      : "0 4px 12px rgba(89,65,37,0.07), inset 0 1px 0 rgba(255,255,255,0.48)",
  };
  const primaryTrack = [...reputationTracks].sort((a, b) => {
    const tierDelta =
      (REPUTATION_TIER_ORDER_LOCAL[b.tier] || 0) -
      (REPUTATION_TIER_ORDER_LOCAL[a.tier] || 0);
    return tierDelta || (b.value || 0) - (a.value || 0);
  })[0];

  const earnedMedals = React.useMemo(() => {
    const medals: Array<{
      key: string;
      icon: string;
      label: string;
      detail: string;
      tone: "gold" | "blue" | "mint" | "violet" | "bronze";
    }> = [];

    if (isFounding5k)
      medals.push({
        key: "founding-5k",
        icon: "🌊",
        label: "Founding 5K",
        detail: "Founder tide",
        tone: "gold",
      });
    if (safeLevel >= 4)
      medals.push({
        key: "level-frame",
        icon: prestigeCopy.icon,
        label: prestigeCopy.label,
        detail: `Lv ${safeLevel}`,
        tone:
          profilePrestigeTier === "gold" || profilePrestigeTier === "mythic"
            ? "gold"
            : "blue",
      });
    if (completedChallengesCount > 0)
      medals.push({
        key: "challenge-clears",
        icon: "🎯",
        label: "Challenge Clears",
        detail: `${completedChallengesCount} done`,
        tone: "violet",
      });

    featuredBadges.forEach((badge) => {
      medals.push({
        key: badge.key,
        icon: badge.icon,
        label: badge.shortLabel || badge.label,
        detail: TRACK_LABELS[badge.track],
        tone:
          badge.tier === "gold" || badge.tier === "mythic"
            ? "gold"
            : badge.tier === "silver" || badge.tier === "platinum"
              ? "blue"
              : "bronze",
      });
    });

    if (!medals.length)
      medals.push({
        key: "starter",
        icon: "🪵",
        label: "Starter Frame",
        detail: "Log catches to evolve",
        tone: "bronze",
      });

    const seen = new Set<string>();
    return medals
      .filter((m) => {
        if (seen.has(m.key)) return false;
        seen.add(m.key);
        return true;
      })
      .slice(0, 5);
  }, [
    completedChallengesCount,
    featuredBadges,
    isFounding5k,
    prestigeCopy.icon,
    prestigeCopy.label,
    profilePrestigeTier,
    safeLevel,
  ]);

  const medalToneStyle = (
    tone: "gold" | "blue" | "mint" | "violet" | "bronze",
  ): React.CSSProperties => {
    const darkBoost = { textShadow: "0 1px 2px rgba(0,0,0,0.32)" };
    const lightBoost = {
      textShadow: "0 1px 2px rgba(0,0,0,0.20)",
      boxShadow: "0 6px 0 rgba(0,0,0,0.18), 0 12px 24px rgba(0,0,0,0.10)",
    };
    if (tone === "gold")
      return {
        background: isDark
          ? "linear-gradient(180deg, rgba(255,215,106,0.26), rgba(255,215,106,0.15))"
          : "linear-gradient(180deg, #6B4B00, #3B2C00)",
        borderColor: isDark
          ? "rgba(255,215,106,0.48)"
          : "rgba(255,224,138,0.70)",
        color: isDark ? "#FFF6CF" : "#FFF1B6",
        ...(isDark ? darkBoost : lightBoost),
      };
    if (tone === "blue")
      return {
        background: isDark
          ? "linear-gradient(180deg, rgba(124,199,255,0.25), rgba(124,199,255,0.13))"
          : "linear-gradient(180deg, #0E5B85, #123A5C)",
        borderColor: isDark
          ? "rgba(124,199,255,0.45)"
          : "rgba(191,230,255,0.70)",
        color: isDark ? "#F0FAFF" : "#EAF8FF",
        ...(isDark ? darkBoost : lightBoost),
      };
    if (tone === "mint")
      return {
        background: isDark
          ? "linear-gradient(180deg, rgba(124,240,196,0.24), rgba(124,240,196,0.13))"
          : "linear-gradient(180deg, #145C42, #0F3F31)",
        borderColor: isDark
          ? "rgba(124,240,196,0.43)"
          : "rgba(183,255,226,0.70)",
        color: isDark ? "#F0FFF8" : "#DFFFEF",
        ...(isDark ? darkBoost : lightBoost),
      };
    if (tone === "violet")
      return {
        background: isDark
          ? "linear-gradient(180deg, rgba(196,181,253,0.26), rgba(196,181,253,0.14))"
          : "linear-gradient(180deg, #5B2E91, #3B236F)",
        borderColor: isDark
          ? "rgba(196,181,253,0.46)"
          : "rgba(239,227,255,0.70)",
        color: isDark ? "#FAF5FF" : "#F7F0FF",
        ...(isDark ? darkBoost : lightBoost),
      };
    return {
      background: isDark
        ? "linear-gradient(180deg, rgba(255,235,205,0.17), rgba(255,255,255,0.09))"
        : "linear-gradient(180deg, #684A2D, #3C3024)",
      borderColor: isDark ? "rgba(255,235,205,0.28)" : "rgba(255,235,205,0.62)",
      color: isDark ? "#FFF4E3" : "#FFF4E3",
      ...(isDark ? darkBoost : lightBoost),
    };
  };

  const prestigeHeroStyle: React.CSSProperties = {
    background: ui.cardBgStrong ?? ui.cardBg,
    borderColor: ui.borderStrong ?? ui.border,
    boxShadow: isDark
      ? "0 9px 22px rgba(0,0,0,0.22)"
      : "0 7px 18px rgba(89,65,37,0.07)",
  };

  const prestigeAvatarFrameStyle: React.CSSProperties = hasEquippedProfileFrame
    ? {
        background: prestigePalette.frame,
        boxShadow: isDark
          ? `0 14px 0 rgba(0,0,0,0.24), 0 0 34px ${prestigePalette.glow}, inset 0 2px 0 rgba(255,255,255,0.22)`
          : `0 10px 0 rgba(132,96,31,0.22), 0 0 30px ${prestigePalette.glow}, inset 0 2px 0 rgba(255,255,255,0.60)`,
      }
    : neutralAvatarFrameStyle;

  const tierDots = Array.from(
    { length: 5 },
    (_, idx) => idx <= PROFILE_TIER_ORDER[profilePrestigeTier],
  );

  const fieldProgressCard = (
      <div className={creamCard}>
          <div className={`${innerCard} p-4`}>
            <div className="relative space-y-3">
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] border border-black/10 bg-[var(--profile-sand)] text-xl shadow-[0_6px_0_rgba(0,0,0,0.10)] dark:border-white/10 dark:from-[#7C3AED]/35 dark:via-[#10B981]/22 dark:to-[#38BDF8]/24">
                    🏆
                  </div>
                  <div className="min-w-0">
                    <div className={sectionEyebrow}>Field progress</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="text-2xl font-black leading-none text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                        Level {safeLevel}
                      </span>
                      <span className="rounded-full border border-[#0F6B49]/35 bg-[#145C42] px-3 py-1 text-[10px] font-black text-[#DFFFEF] shadow-[0_4px_0_rgba(15,107,73,0.14)] dark:border-[#A7F3D0]/70 dark:bg-[#DFFFEF] dark:text-[#0F5137]">
                        {anglerRank}
                      </span>
                    </div>
                    <p className="mt-1 text-[12px] font-semibold leading-relaxed text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                      {xpInfo.bandLabel}
                    </p>
                  </div>
                </div>

                <div className="rounded-[14px] border border-black/10 bg-[var(--profile-surface-bg)] px-4 py-2.5 text-left shadow-[0_7px_0_rgba(0,0,0,0.07)] dark:border-white/10 dark:bg-[#0F1828] sm:min-w-[132px] sm:text-right">
                  <div className="text-[10px] font-black uppercase tracking-[0.16em] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                    Lifetime XP
                  </div>
                  <div className="mt-1 text-xl font-black tabular-nums text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                    {canonicalXP.toLocaleString()}
                  </div>
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3 text-[11px] font-bold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                  <span>
                    {Math.round(clampPct(progressPercentage))}% through Level{" "}
                    {safeLevel}
                  </span>
                  <span className="font-semibold">
                    {xpInfo.gained.toLocaleString()} /{" "}
                    {levelSpan.toLocaleString()} XP
                  </span>
                </div>

                <div className="relative h-3 overflow-hidden rounded-full border border-black/10 bg-[#E8E0D4] shadow-[0_6px_0_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-white/10">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-[var(--profile-accent)] transition-[width] duration-700 ease-out"
                    style={{ width: `${clampPct(progressPercentage)}%` }}
                  >
                  </div>
                </div>
              </div>

              <div className="space-y-2.5">
                <div className="rounded-[18px] border border-black/10 bg-[var(--profile-surface-bg)] px-3.5 py-3 shadow-[0_4px_12px_rgba(89,65,37,0.06)] dark:border-white/10 dark:bg-[#0F1828]">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--profile-accent-border)] bg-[var(--profile-sand)] text-base text-[var(--profile-accent)] shadow-[0_4px_10px_rgba(89,65,37,0.07)]">
                        🌊
                      </span>
                      <div className="min-w-0">
                        <div className={sectionEyebrow}>Next level</div>
                        <div className="text-[11px] font-bold leading-snug text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                          XP needed to reach Level {safeLevel + 1}
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 text-right text-xl font-black tabular-nums text-[var(--profile-accent)]">
                      {xpToNext.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="rounded-[18px] border border-black/10 bg-[var(--profile-surface-bg)] px-3.5 py-3 shadow-[0_4px_12px_rgba(89,65,37,0.06)] dark:border-white/10 dark:bg-[#0F1828]">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-black/10 bg-[var(--profile-sand)] text-base text-[var(--profile-text-primary)] shadow-[0_4px_10px_rgba(89,65,37,0.07)] dark:border-white/10">
                        {primaryTrack?.key === "species_mastery" ? (
                          <RoddoFishIcon mode="png" animated={false} className="h-6 w-7 object-contain" />
                        ) : primaryTrack ? (
                          getTrackIconFromKey(primaryTrack.key)
                        ) : (
                          "✨"
                        )}
                      </span>
                      <div className="min-w-0">
                        <div className={sectionEyebrow}>Top track</div>
                        <div className="text-[11px] font-bold leading-snug text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                          Strongest reputation path so far
                        </div>
                      </div>
                    </div>
                    <div className="min-w-[92px] max-w-[46%] shrink-0 text-right text-sm font-black leading-tight text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                      {primaryTrack
                        ? TRACK_LABELS[primaryTrack.key]
                        : "Starter"}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleManageAvatarGear}
                  className="w-full rounded-[14px] border border-black/10 bg-[var(--profile-surface-bg)] px-3.5 py-3 text-left shadow-[0_3px_10px_rgba(89,65,37,0.06)] transition hover:-translate-y-px active:translate-y-0 dark:border-white/10 dark:bg-[#0F1828]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#D9B86C]/45 bg-[#F5E7C5] text-base text-[#8B6400] shadow-[0_4px_10px_rgba(137,100,0,0.08)] dark:border-[#F2B84B]/25 dark:bg-[rgba(242,184,75,0.12)] dark:text-[#F2D58A]">
                        🐚
                      </span>
                      <div className="min-w-0">
                        <div className={sectionEyebrow}>Shell wallet</div>
                        <div className="text-[11px] font-bold leading-snug text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                          {shellBalance > 0 || lifetimeShells > 0
                            ? `${lifetimeShells.toLocaleString()} lifetime earned from Roddo progress`
                            : "Earned from logs, goals, and milestones — not cash or Bazaar money"}
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-xl font-black tabular-nums text-[#8B6400] dark:text-[#F2D58A]">
                        {shellBalance.toLocaleString()}
                      </div>
                      <div className="mt-0.5 text-[10px] font-black uppercase tracking-[0.12em] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                        Open Hold →
                      </div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setShowReputationDetails((v) => !v)}
                  className="w-full rounded-[14px] border border-black/10 bg-[var(--profile-surface-bg)] px-3.5 py-3 text-left shadow-[0_3px_10px_rgba(89,65,37,0.06)] transition hover:-translate-y-px active:translate-y-0 dark:border-white/10 dark:bg-[#0F1828]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-black/10 bg-[var(--profile-sand)] text-base text-[var(--profile-text-primary)] shadow-[0_4px_10px_rgba(89,65,37,0.07)] dark:border-white/10">
                        📈
                      </span>
                      <div className="min-w-0">
                        <div className={sectionEyebrow}>Progress hall</div>
                        <div className="text-[11px] font-bold leading-snug text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                          Reputation track details
                        </div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 text-right">
                      <span className="text-sm font-black leading-tight text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                        {reputationTracks.length} tracks
                      </span>
                      <ChevronDownIcon
                        className={`h-4 w-4 shrink-0 text-[var(--roddo-text-light-soft)] transition-transform dark:text-[var(--roddo-text-dark-soft)] ${showReputationDetails ? "rotate-180" : ""}`}
                      />
                    </div>
                  </div>
                </button>
              </div>

              {showReputationDetails ? (
                <div className="rounded-[14px] border border-black/10 bg-[var(--profile-surface-bg)] p-3.5 shadow-[0_5px_14px_rgba(89,65,37,0.06)] dark:border-white/10 dark:bg-[#0F1828]">
                  {!!featuredBadges.length ? (
                    <div className="mb-4">
                      <div className={`${sectionEyebrow} mb-2`}>
                        Achievement cabinet
                      </div>
                      <ReputationRibbon reputation={reputation} />
                    </div>
                  ) : null}

                  <div className="space-y-2.5">
                    {reputationTracks.map((track) => (
                      <ReputationTrackCard key={track.key} track={track} />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

      </div>
  );

  return (
    <div
      className={`${isDark ? "dark" : ""} mx-auto max-w-5xl space-y-3 px-3 pb-[calc(var(--roddo-nav-h,76px)+18px)] pt-3 sm:px-4 sm:pt-4 lg:pb-8`}
      data-roddo-theme={isDark ? "dark" : "light"}
      style={{ ...profileVars, background: ui.pageBg ?? "transparent" }}
    >
      <RoddoPillTabs
        items={[
          { key: "overview", label: "Profile" },
          { key: "avatar", label: "Avatar" },
        ]}
        activeKey={profilePage}
        onChange={(key) => setProfilePage(key === "avatar" ? "avatar" : "overview")}
        ariaLabel="Profile sections"
      />

      {profilePage === "overview" ? (
        <>
      <div className={creamCard}>
          <div
            className="relative overflow-hidden rounded-[14px] border p-4 sm:p-5"
            style={prestigeHeroStyle}
          >

            <div className="relative z-[1]">
              <div className="absolute right-0 top-0 z-20">
                <KebabMenu
                  items={[
                    {
                      label: t("profile.edit", "Edit profile"),
                      onClick: () => onEditProfile?.(),
                    },
                  ]}
                />
              </div>

              <div className="mb-3 flex flex-wrap items-center gap-2 pr-12">
                <span
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]"
                  style={{
                    background: isDark
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(255,255,255,0.54)",
                    borderColor: prestigePalette.border,
                    color: prestigePalette.text,
                  }}
                >
                  <span>{prestigeCopy.icon}</span>
                  <span>Angler passport</span>
                </span>
                <span
                  className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em]"
                  style={{
                    background: isDark
                      ? "rgba(0,0,0,0.18)"
                      : "rgba(255,255,255,0.58)",
                    borderColor: prestigePalette.border,
                    color: prestigePalette.text,
                  }}
                >
                  {profileFramePillLabel}
                </span>
              </div>

              <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
                <div className="flex flex-col items-center gap-2 sm:items-start">
                  <div
                    className={`relative rounded-[30px] ${hasEquippedProfileFrame ? "p-[5px]" : "p-[3px]"}`}
                    style={prestigeAvatarFrameStyle}
                  >
                    <div
                      className="absolute -left-2 -top-2 grid h-8 w-8 place-items-center rounded-full border border-white/40 text-sm shadow-[0_5px_0_rgba(0,0,0,0.14)]"
                      style={{
                        background: hasEquippedProfileFrame
                          ? prestigePalette.frame
                          : isDark
                            ? "rgba(255,255,255,0.10)"
                            : "rgba(255,255,255,0.82)",
                      }}
                    >
                      {hasEquippedProfileFrame ? prestigeCopy.icon : "🎣"}
                    </div>
                    <div
                      className="absolute -bottom-2 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em]"
                      style={{
                        background: isDark
                          ? "rgba(20,18,42,0.90)"
                          : "rgba(255,255,255,0.90)",
                        borderColor: prestigePalette.border,
                        color: prestigePalette.text,
                      }}
                    >
                      Lv {safeLevel}
                    </div>
                    <div className="h-20 w-20 overflow-hidden rounded-[14px] border-[3px] border-white/60 bg-[var(--profile-surface-bg)] shadow-[inset_0_2px_0_rgba(255,255,255,0.35)] sm:h-20 sm:w-20">
                      {viewedUserAvatar ? (
                        <img
                          key={viewedUserAvatar}
                          src={viewedUserAvatar}
                          alt={userProfile.name || "Angler"}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="grid h-full w-full place-items-center bg-[var(--profile-accent-soft)] text-5xl">
                          🎣
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {tierDots.map((active, idx) => (
                      <span
                        key={idx}
                        className="h-2.5 w-2.5 rounded-full border"
                        style={{
                          background: active
                            ? prestigePalette.frame
                            : isDark
                              ? "rgba(255,255,255,0.10)"
                              : "rgba(0,0,0,0.08)",
                          borderColor: active
                            ? prestigePalette.border
                            : isDark
                              ? "rgba(255,255,255,0.14)"
                              : "rgba(0,0,0,0.10)",
                        }}
                      />
                    ))}
                  </div>
                </div>

                <div className="min-w-0 text-center sm:text-left">
                  <div
                    className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-black shadow-[0_4px_0_rgba(0,0,0,0.13),0_9px_18px_rgba(0,0,0,0.08)]"
                    style={{
                      background: isDark ? "#DFFFEF" : "#145C42",
                      borderColor: isDark
                        ? "rgba(167,243,208,0.70)"
                        : "rgba(15,107,73,0.62)",
                      color: isDark ? "#0F5137" : "#DFFFEF",
                    }}
                  >
                    <span className="inline-flex items-center justify-center">
                      {primaryTrack?.key === "species_mastery" ? (
                        <RoddoFishIcon mode="png" animated={false} className="h-5 w-6 object-contain" />
                      ) : primaryTrack ? (
                        getTrackIconFromKey(primaryTrack.key)
                      ) : (
                        "✨"
                      )}
                    </span>
                    <span>{prestigeCopy.title}</span>
                  </div>

                  <h1
                    className="mt-2 break-words text-[30px] font-black leading-[0.95] tracking-[-0.055em] text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)] sm:text-[40px]"
                    title={userProfile.name || t("profile.angler", "Angler")}
                  >
                    {userProfile.name || t("profile.angler", "Angler")}
                  </h1>

                  {viewedUserHandle ? (
                    <div className="mt-1 text-[12px] font-black text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                      @{viewedUserHandle}
                    </div>
                  ) : null}

                  <p className="mx-auto mt-3 max-w-2xl whitespace-pre-line text-[13px] font-semibold leading-5 text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)] sm:text-sm sm:leading-6 sm:mx-0">
                    {publicBio ||
                      prestigeCopy.subtitle ||
                      t(
                        "profile.subtitle",
                        "Your fishing identity, water history, and trusted essentials — all in one clean deck.",
                      )}
                  </p>


                </div>
              </div>






            </div>
          </div>


      </div>
      <div className={creamCard}>
              <div
                className="rounded-[16px] border p-3"
                style={{
                  background: isDark
                    ? "#0C1423"
                    : "rgba(255,255,255,0.44)",
                  borderColor: prestigePalette.border,
                }}
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className={sectionEyebrow}>Featured marks</div>
                  <div className="text-[10px] font-extrabold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                    Top 3 shown
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {earnedMedals.slice(0, 3).map((medal) => (
                    <span
                      key={medal.key}
                      className="inline-flex min-w-0 items-center gap-2 rounded-full border px-3 py-2 text-[10px] font-black shadow-[0_5px_0_rgba(0,0,0,0.07)]"
                      style={medalToneStyle(medal.tone)}
                    >
                      <span className="text-sm leading-none">{medal.icon}</span>
                      <span className="truncate">{medal.label}</span>
                    </span>
                  ))}
                </div>
              </div>
      </div>
      {(userProfile as any)?.accountType === 'guest' ? (
        <button
          type="button"
          onClick={() => onEditProfile?.()}
          className={`${creamCard} w-full text-left transition hover:-translate-y-0.5 active:translate-y-[1px]`}
          style={{
            background: ui.cardBgStrong ?? ui.cardBg,
            borderColor: isDark ? 'rgba(112,87,232,0.28)' : 'rgba(47,128,237,0.22)',
          }}
        >
          <div className="relative z-[1] flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className={sectionEyebrow}>Guest passport</div>
              <div className="mt-1 text-base font-black text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                Keep this Roddo profile
              </div>
              <p className="mt-1 text-[12px] font-semibold leading-5 text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                Link Apple or Google so your catches, Shells, gear, and fishing history stay with this same account.
              </p>
            </div>
            <span className="shrink-0 rounded-full border px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em]" style={{ borderColor: prestigePalette.border, color: prestigePalette.text }}>
              Secure →
            </span>
          </div>
        </button>
      ) : null}

      <div className={creamCard} style={licenseCardShellStyle}>

        <div className="relative z-[1] space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="grid h-10 w-10 shrink-0 place-items-center rounded-[14px] border text-base shadow-none"
                style={{
                  background: isDark
                    ? "rgba(103,216,232,0.08)"
                    : "rgba(47,128,237,0.09)",
                  borderColor: isDark
                    ? "rgba(124,199,255,0.26)"
                    : "rgba(124,199,255,0.42)",
                }}
              >
                🪪
              </span>
              <div className="min-w-0">
                <div className={sectionEyebrow}>Verified licenses</div>
                <h2 className="mt-1 text-lg font-black leading-tight text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                  {activeLicense ? "License passport" : "License vault"}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {activeLicense ? (
                <span
                  className="rounded-full px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.12em]"
                  style={sectionHeroChip(
                    isDark
                      ? "linear-gradient(180deg, rgba(124,240,196,0.18), rgba(103,216,255,0.12))"
                      : "rgba(47,128,237,0.09)",
                    isDark ? "#DFF6EA" : "#145C42",
                  )}
                >
                  Ready
                </span>
              ) : null}

              <KebabMenu
                items={[
                  {
                    label: t("profile.addFindLicense", "Add / find license"),
                    onClick: () => setIsAddLicenseModalOpen(true),
                  },
                  ...(activeLicense
                    ? [
                        {
                          label: t("profile.editLicense", "Edit license"),
                          onClick: () => {
                            if (onViewLicenseDetail) {
                              onViewLicenseDetail(activeLicense as any);
                              return;
                            }
                            onLicenseClick?.(activeLicense as any);
                          },
                        },
                      ]
                    : []),
                  {
                    label: t("profile.findOfficialLicenseSite", "Find official license site"),
                    onClick: () => setIsLicenseModalOpen(true),
                  },
                ]}
              />
            </div>
          </div>

          {activeLicense ? (
            <button
              type="button"
              onClick={() => onLicenseClick?.(activeLicense as any)}
              className="group grid w-full grid-cols-[88px_minmax(0,1fr)] gap-3 overflow-hidden rounded-[16px] border p-3 text-left transition hover:bg-black/[0.02] dark:hover:bg-white/[0.025] sm:grid-cols-[96px_minmax(0,1fr)]"
              style={{
                background: isDark
                  ? "#0F1828"
                  : "#FFFCF7",
                borderColor: isDark
                  ? "rgba(132,151,190,0.16)"
                  : "rgba(124,199,255,0.28)",
                boxShadow: isDark
                  ? "0 4px 12px rgba(0,0,0,0.12)"
                  : "0 4px 12px rgba(70,112,95,0.06)",
              }}
            >
              <div className="relative aspect-square min-h-[88px] overflow-hidden rounded-[12px] border border-white/15 bg-black/10 shadow-none">
                {getLicensePhoto(activeLicense) ? (
                  <img
                    src={getLicensePhoto(activeLicense)}
                    alt={t("profile.licenseAlt", "License")}
                    className="h-full w-full object-cover"
                  />
                ) : viewedUserAvatar ? (
                  <img
                    key={viewedUserAvatar}
                    src={viewedUserAvatar}
                    alt={t("profile.licenseHolderAlt", "License holder")}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="grid h-full min-h-[88px] place-items-center text-3xl">
                    🪪
                  </div>
                )}
                <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center px-1.5">
                  <span className="inline-flex max-w-full items-center justify-center whitespace-nowrap rounded-full border border-black/10 bg-white/92 px-2.5 py-1 text-[8px] font-black uppercase leading-none tracking-[0.10em] text-slate-700 shadow-[0_2px_8px_rgba(0,0,0,0.16)]">
                    {t("profile.tapToView", "Tap to view")}
                  </span>
                </div>
              </div>

              <div className="min-w-0 py-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                      {t("profile.activeLicense", "Active license")}
                    </div>
                    <div className="mt-1 truncate text-base font-black text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                      {activeLicense.state || t("profile.stateOnFile", "State on file")}
                    </div>
                    <div className="mt-0.5 truncate text-[12px] font-bold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                      {activeLicense.type || t("profile.fishingLicense", "Fishing License")}
                    </div>
                  </div>
                  <span
                    className={`rounded-full border px-3 py-1 text-[10px] font-black ${statusBadge(!!activeLicense.active)}`}
                  >
                    {t("profile.active", "Active")}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="rounded-[12px] border border-black/10 bg-[var(--profile-surface-bg)] px-3 py-2 dark:border-[rgba(132,151,190,0.12)] dark:bg-[#0C1423]">
                    <div className="text-[9px] font-black uppercase tracking-[0.16em] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                      {t("profile.licenseNumberShort", "License #")}
                    </div>
                    <div className="mt-1 truncate text-[12px] font-black text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                      {maskId(
                        (activeLicense as any).licenseNumber ||
                          activeLicense.id,
                      )}
                    </div>
                  </div>
                  <div className="rounded-[12px] border border-black/10 bg-[var(--profile-surface-bg)] px-3 py-2 dark:border-[rgba(132,151,190,0.12)] dark:bg-[#0C1423]">
                    <div className="text-[9px] font-black uppercase tracking-[0.16em] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                      {t("profile.expires", "Expires")}
                    </div>
                    <div className="mt-1 truncate text-[12px] font-black text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                      {(activeLicense as any).expiry ||
                        (activeLicense as any).expiryDate ||
                        t("profile.noDate", t("profile.noDate", "No date"))}
                    </div>
                  </div>
                </div>

              </div>
            </button>
          ) : (
            <div
              className={`${innerCard} p-4 text-sm text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]`}
            >
              {t("profile.noLicenses", "No licenses saved yet. Add one to keep your trip docs close.")}
            </div>
          )}

          {(userLicenses || []).length > 1 ? (
            <div className="space-y-2">
              {(userLicenses || [])
                .filter(
                  (license: any) => license.id !== (activeLicense as any)?.id,
                )
                .slice(0, 2)
                .map((license: any) => (
                  <button
                    key={String(license.id)}
                    type="button"
                    onClick={() => onLicenseClick?.(license)}
                    className={rowButton}
                    style={licenseRowStyle}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 text-left">
                        <div className="truncate text-sm font-extrabold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                          {(license.state || t("profile.state", "State")) +
                            " — " +
                            (license.type || t("profile.fishing", "Fishing"))}
                        </div>
                        <div className="mt-1 text-[11px] font-semibold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                          #
                          {maskId((license as any).licenseNumber || license.id)}{" "}
                          · {t("profile.expires", "Expires")}{" "}
                          {(license as any).expiry ||
                            (license as any).expiryDate ||
                            t("profile.noDate", "No date")}
                        </div>
                      </div>
                      <div
                        className={`rounded-full border px-3 py-1 text-[11px] font-extrabold ${statusBadge(!!license.active)}`}
                      >
                        {license.active
                          ? t("profile.active", "Active")
                          : t("profile.expired", "Expired")}
                      </div>
                    </div>
                  </button>
                ))}
            </div>
          ) : null}
        </div>
      </div>

      {SHOW_MARKETPLACE ? (
        <div className={creamCard}>
          <div className="text-sm font-extrabold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
            {t("profile.marketplace", "Marketplace")}
          </div>
        </div>
      ) : null}

      <div className={creamCard} style={gearCardShellStyle}>

        <div className="relative z-[1] space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="grid h-10 w-10 shrink-0 place-items-center rounded-[14px] border text-base shadow-none"
                style={{
                  background: isDark
                    ? "rgba(102,214,169,0.10)"
                    : "rgba(47,128,237,0.09)",
                  borderColor: isDark
                    ? "rgba(124,240,196,0.22)"
                    : "rgba(124,240,196,0.32)",
                }}
              >
                🎒
              </span>
              <div className="min-w-0">
                <div className={sectionEyebrow}>{t("profile.loadout", "Loadout")}</div>
                <h2 className="mt-1 text-lg font-black leading-tight text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                  {t("profile.activeKit", "Active kit")}
                </h2>
                <p className="mt-0.5 text-[12px] font-semibold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                  {equippedSlotCount > 0
                    ? t("profile.equippedReady", "{{count}} equipped essentials ready.", { count: equippedSlotCount })
                    : t(
                        "profile.gearHint",
                        "Equip your go-to rod, reel, and lure.",
                      )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div
                className={`${chip} rounded-2xl`}
                style={sectionHeroChip(
                  isDark
                    ? "rgba(102,214,169,0.10)"
                    : "rgba(47,128,237,0.09)",
                  isDark ? "#BDEBD6" : "#145C42",
                )}
              >
                <span className="text-[11px] font-bold">Equipped</span>
                <span className="text-[12px] font-extrabold">
                  {equippedSlotCount}/3
                </span>
              </div>
              <KebabMenu
                items={[{ label: "Manage kit", onClick: handleManageGear }]}
              />
            </div>
          </div>

          <div className="space-y-3">
            {(["rod", "reel", "lure"] as const).map((slot) => {
              const label =
                slot === "rod" ? "Rod" : slot === "reel" ? "Reel" : "Lure";
              const icon =
                slot === "rod" ? "🎣" : slot === "reel" ? "🧵" : "🪱";
              const item = (equippedInfo as any)[slot];
              const has = !!item?.name;
              const open = gearExpanded === slot;
              const accent =
                slot === "rod"
                  ? "#7CC7FF"
                  : slot === "reel"
                    ? "#7CF0C4"
                    : "#FFD76A";

              return (
                <div
                  key={slot}
                  className="overflow-hidden rounded-[14px] border transition-[background,border-color,color] duration-200"
                  style={{
                    background: isDark
                      ? "#0F1828"
                      : "rgba(255,255,255,0.62)",
                    borderColor: isDark
                      ? "rgba(132,151,190,0.14)"
                      : "rgba(116,86,54,0.14)",
                    boxShadow: "none",
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setGearExpanded((prev) => (prev === slot ? null : slot))
                    }
                    className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition hover:bg-black/[0.03] dark:hover:bg-white/[0.025]"
                  >
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-[12px] border text-sm"
                      style={{
                        background: isDark
                          ? "#0C1423"
                          : "#F3F7FF",
                        borderColor: isDark
                          ? "rgba(132,151,190,0.12)"
                          : "rgba(0,0,0,0.08)",
                      }}
                    >
                      {icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                          {label}
                        </span>
                        {has ? (
                          <span className="rounded-full border border-emerald-300/20 bg-emerald-300/10 px-2 py-0.5 text-[9px] font-black uppercase text-emerald-700 dark:text-[#BDEBD6]">
                            Equipped
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 truncate text-sm font-black text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                        {has
                          ? item.name
                          : t("profile.noneEquipped", "None equipped yet")}
                      </div>
                    </div>
                    <ChevronDownIcon
                      className={`h-4 w-4 shrink-0 text-[var(--roddo-text-light-soft)] transition-transform dark:text-[var(--roddo-text-dark-soft)] ${open ? "rotate-180" : ""}`}
                    />
                  </button>

                  {open ? (
                    <div className="px-4 pb-4">
                      <div className="rounded-[12px] border border-black/10 bg-[var(--profile-surface-bg)] p-3 shadow-none dark:border-[rgba(132,151,190,0.12)] dark:bg-[#0C1423]">
                        <div className={sectionEyebrow}>Equipped</div>
                        <div className="mt-1 text-sm font-extrabold text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                          {has
                            ? item.name
                            : t("profile.noneEquipped", "None equipped yet")}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className={creamCard}>
              <button
                type="button"
                onClick={handleOpenRoddoRadio}
                className="w-full rounded-[16px] border p-3 text-left shadow-[0_5px_14px_rgba(89,65,37,0.06)] transition hover:-translate-y-0.5 active:translate-y-px"
                style={{
                  background: ui.surfaceBgSoft ?? ui.cardBg,
                  borderColor: ui.border,
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-[var(--profile-accent)] text-xl shadow-[0_5px_0_rgba(0,0,0,0.14)]">
                      📻
                    </span>
                    <div className="min-w-0">
                      <div className={sectionEyebrow}>Roddo Radio</div>
                      <div className="mt-1 text-[12px] font-bold leading-snug text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                        Original fishing music for your map, log, and memories.
                      </div>
                    </div>
                  </div>
                  <span
                    className="shrink-0 rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em]"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.74)",
                      borderColor: prestigePalette.border,
                      color: prestigePalette.text,
                    }}
                  >
                    Listen
                  </span>
                </div>
              </button>
      </div>
        </>
      ) : null}

      {profilePage === "avatar" ? (
        <>
          <div className={creamCard}>
            <div
              className="relative overflow-hidden rounded-[14px] border p-4 sm:p-5"
              style={{
                background: ui.cardBgStrong ?? ui.cardBg,
                borderColor: ui.borderStrong ?? ui.border,
              }}
            >

              <div className="relative z-[1] space-y-4">
                <div className="grid grid-cols-[104px_minmax(0,1fr)] items-center gap-4 min-[520px]:grid-cols-[116px_minmax(0,1fr)]">
                  <div className="flex justify-start">
                    <div
                      className={`relative rounded-[32px] ${hasEquippedProfileFrame ? "p-[5px]" : "p-[3px]"}`}
                      style={prestigeAvatarFrameStyle}
                    >
                      <div className="h-[104px] w-[104px] overflow-hidden rounded-[28px] border-[3px] border-white/60 bg-[var(--profile-surface-bg)] shadow-[inset_0_2px_0_rgba(255,255,255,0.35)] min-[520px]:h-[116px] min-[520px]:w-[116px] min-[520px]:rounded-[30px]">
                        {viewedUserAvatar ? (
                          <img
                            key={viewedUserAvatar}
                            src={viewedUserAvatar}
                            alt={userProfile.name || "Angler"}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center bg-[var(--profile-accent-soft)] text-4xl">
                            🎣
                          </div>
                        )}
                      </div>

                      <div
                        className="absolute -bottom-2 left-1/2 z-20 max-w-[132px] -translate-x-1/2 truncate whitespace-nowrap rounded-full border px-2.5 py-1 text-[8px] font-black uppercase tracking-[0.10em]"
                        style={{
                          background: isDark ? "#0C1423" : "rgba(255,255,255,0.92)",
                          borderColor: prestigePalette.border,
                          color: prestigePalette.text,
                        }}
                        title={equippedFrameCosmetic ? equippedFrameCosmetic.name : prestigeCopy.label}
                      >
                        {equippedFrameCosmetic ? equippedFrameCosmetic.name : prestigeCopy.label}
                      </div>
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className={sectionEyebrow}>Avatar locker</div>
                    <h2 className="mt-1 text-[19px] font-black leading-tight text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                      Your Roddo look
                    </h2>
                    <p className="mt-1 max-w-[310px] text-[11px] font-semibold leading-[1.55] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                      Build your angler identity with frames, outfits, titles, and earned cosmetics.
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span
                        className="inline-flex items-center rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.10em]"
                        style={{
                          background: isDark ? "rgba(138,112,242,0.11)" : "#F3E8D8",
                          borderColor: prestigePalette.border,
                          color: prestigePalette.text,
                        }}
                      >
                        {featuredAvatarGear.length} equipped
                      </span>
                      <span className="text-[10px] font-bold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                        {equippedFrameCosmetic ? equippedFrameCosmetic.name : prestigeCopy.label}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleManageAvatarGear}
                  className="w-full rounded-[12px] border px-4 py-2.5 text-[11px] font-black shadow-[0_5px_14px_rgba(47,128,237,0.14)] transition hover:brightness-[1.03] active:translate-y-px"
                  style={{
                    background: isDark ? "#8A70F2" : "#2F80ED",
                    borderColor: isDark ? "rgba(174,158,255,0.28)" : "rgba(47,128,237,0.22)",
                    color: "#FFFFFF",
                  }}
                >
                  Open The Hold
                </button>
              </div>
            </div>
          </div>
              <div
                className="rounded-[18px] border p-3"
                style={{
                  background: isDark
                    ? "#0C1423"
                    : "rgba(255,255,255,0.44)",
                  borderColor: prestigePalette.border,
                }}
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className={sectionEyebrow}>Avatar gear</div>
                  <div className="text-[10px] font-extrabold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                    Equipped loadout
                  </div>
                </div>
                <div className="space-y-2">
                  {featuredAvatarGear.slice(0, 5).map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-3 rounded-[14px] border px-3 py-2"
                      style={{
                        background: isDark
                          ? "#0F1828"
                          : "rgba(255,255,255,0.56)",
                        borderColor: isDark
                          ? "rgba(255,255,255,0.10)"
                          : "rgba(0,0,0,0.08)",
                      }}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/80 text-base shadow-[0_4px_0_rgba(0,0,0,0.09)] dark:bg-white/12">
                          {item.emoji}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-[12px] font-black text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                            {item.name}
                          </div>
                          <div className="text-[10px] font-bold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                            {getCosmeticSlotLabel(item.slot as CosmeticSlot)}
                          </div>
                        </div>
                      </div>
                      <span
                        className="rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em]"
                        style={{
                          borderColor: prestigePalette.border,
                          color: prestigePalette.text,
                        }}
                      >
                        {item.rarity}
                      </span>
                    </div>
                  ))}
                </div>
                {previewAvatarShop.length ? (
                  <div
                    className="mt-3 rounded-[14px] border px-3 py-2"
                    style={{
                      background: isDark
                        ? "rgba(138,112,242,0.08)"
                        : "#F6EBD9",
                      borderColor: prestigePalette.border,
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div
                          className="text-[10px] font-black uppercase tracking-[0.14em]"
                          style={{ color: prestigePalette.text }}
                        >
                          Gear shop
                        </div>
                        <div className="mt-1 text-[11px] font-bold text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                          {previewAvatarShop[0].emoji}{" "}
                          {previewAvatarShop[0].name} is waiting in The Hold.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleManageAvatarGear}
                        className="shrink-0 rounded-full px-3 py-1.5 text-[10px] font-black"
                        style={sectionHeroChip(
                          isDark ? "rgba(255,255,255,0.10)" : "#FFFFFF",
                          prestigePalette.text,
                        )}
                      >
                        View gear
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
      <div
        className={`${innerCard} relative overflow-hidden p-4 sm:p-4.5`}
        style={{
          background: ui.cardBgStrong ?? ui.cardBg,
          borderColor: ui.borderStrong ?? ui.border,
          boxShadow: isDark
            ? "0 10px 0 rgba(0,0,0,0.16), 0 22px 46px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.08)"
            : "0 10px 0 rgba(70,112,95,0.08), 0 20px 38px rgba(53,108,87,0.10), inset 0 1px 0 rgba(255,255,255,0.60)",
        }}
      >
        <div className="relative space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span
                className="grid h-10 w-10 shrink-0 place-items-center rounded-[14px] border text-base shadow-none"
                style={{
                  background: isDark
                    ? "linear-gradient(180deg, rgba(124,240,196,0.16), rgba(124,199,255,0.12))"
                    : "linear-gradient(180deg, #E7FBF1, #EAF6FF)",
                  borderColor: isDark
                    ? "rgba(124,240,196,0.20)"
                    : "rgba(124,199,255,0.28)",
                }}
              >
                🧠
              </span>
              <div className="min-w-0">
                <div
                  className={`${sectionIconChip} w-fit`}
                  style={analyticsPillStyle(
                    isDark ? "rgba(124,240,196,0.13)" : "#E7FBF1",
                    isDark ? "#DFF6EA" : "#145C42",
                  )}
                >
                  <span>FishIQ</span>
                </div>
                <h2 className="mt-1 text-lg font-black text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                  Personal water read
                </h2>
                <p className="mt-1 max-w-2xl text-[12px] leading-5 text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                  Quiet patterns from your catches, waters, and species.
                </p>
              </div>
            </div>
            <KebabMenu
              items={[
                {
                  label: "Open The Hold",
                  onClick: () => navigateTo(View.TackleBox, { tab: "tips" }),
                },
              ]}
            />
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { icon: "📡", label: "Stage", value: fishIQSummary.signalLabel },
              { icon: "🧪", label: "Sample", value: fishIQSummary.sampleSize },
              {
                icon: "👁️",
                label: "Reads",
                value: fishIQSummary.insightsAvailable,
              },
              {
                icon: "🙈",
                label: "Seen / hidden",
                value: `${fishIQSummary.insightsSeen} / ${fishIQSummary.insightsDismissed}`,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="rounded-[14px] border px-3 py-2.5 transition-[background,border-color,box-shadow,color] duration-300 ease-out"
                style={{ background: ui.cardBgMuted, borderColor: ui.border }}
              >
                <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.14em] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
                <div className="mt-1 truncate text-sm font-black text-[var(--roddo-text-light)] dark:text-[var(--roddo-text-dark)]">
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              ...fishIQSummary.species
                .slice(0, 2)
                .map((item: any) => ({ label: item.label, count: item.count })),
              ...fishIQSummary.windows
                .slice(0, 2)
                .map((item: any) => ({ label: item.label, count: item.count })),
              ...fishIQSummary.conditions
                .slice(0, 2)
                .map((item: any) => ({ label: item.label, count: item.count })),
            ].length ? (
              [
                ...fishIQSummary.species
                  .slice(0, 2)
                  .map((item: any) => ({
                    label: item.label,
                    count: item.count,
                  })),
                ...fishIQSummary.windows
                  .slice(0, 2)
                  .map((item: any) => ({
                    label: item.label,
                    count: item.count,
                  })),
                ...fishIQSummary.conditions
                  .slice(0, 2)
                  .map((item: any) => ({
                    label: item.label,
                    count: item.count,
                  })),
              ].map((item: any) => (
                <span
                  key={`${item.label}-${item.count}`}
                  className={`${chip} text-[10px] font-extrabold`}
                >
                  {item.label} · {item.count}
                </span>
              ))
            ) : (
              <p className="text-[11px] text-[var(--roddo-text-light-soft)] dark:text-[var(--roddo-text-dark-soft)]">
                Log more catches to let FishIQ start finding quiet patterns.
              </p>
            )}
          </div>
        </div>
      </div>


        </>
      ) : null}
      {isLicenseModalOpen ? <OfficialLicenseModal /> : null}

      <AddLicenseModal
        userId={userProfile.uid}
        isOpen={isAddLicenseModalOpen}
        onClose={() => setIsAddLicenseModalOpen(false)}
        onLicenseCreated={() => {
          addToast("Fishing license added.", "success");
          setIsAddLicenseModalOpen(false);
        }}
      />
    </div>
  );
};

export default ProfileView;
